<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef,
    watch,
  } from 'vue';
  import { useDebounceFn } from '@vueuse/core';
  import TranslationsService from '@/api/services/translations.service';
  import V2TranslationEntryRow, {
    ITranslationEntryModel,
  } from '@/components/modals/translations/V2TranslationEntryRow.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import type { IEditorExtra } from '@/composition/catalog/editor-core';
  import type { ISavedSize } from '@/domain/catalog/catalog-filters';
  import {
    changedTextKeys,
    isEmptyTranslationChanges,
    ITranslationText,
    missingNameLanguages,
    rebaseTexts,
    sizeTextKey,
    translationChanges,
    translationStatus,
    translationTexts,
    TTranslatedEntity,
    TTranslationTexts,
  } from '@/domain/catalog/translations-form';
  import { ICategory } from '@/interfaces/ICategory';
  import { IItem } from '@/interfaces/IItem';
  import { ILanguage } from '@/interfaces/ILanguage';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { vueData } from '@/VueData';

  /**
   * «Traducciones» of a product or a category (plan §5.12, §5.15): per
   * chosen language, its name and description, then each saved size of a
   * product. Loaded when the row first opens; the editor's «Guardar» saves
   * it after the entity itself (it registers as an editor extra).
   */
  const props = defineProps<{
    entity: TTranslatedEntity;
    restaurantId: number;
    /** The saved product or category: a new object means it changed. */
    source: IItem | ICategory;
    /** The saved sizes of a product ([] for a category). */
    sizes: ISavedSize[];
    /** The live size option of a product. */
    sizeOptionId?: number | null;
    /** Sizes typed in the editor and not saved yet. */
    hasNewSizes: boolean;
    /** Without `TranslationsManage` the texts are read only. */
    canEdit: boolean;
    /** «Guardar» was tried: errors show. */
    submitted: boolean;
    register: (extra: IEditorExtra) => () => void;
  }>();

  const service = new TranslationsService();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  /** Reloads after the entity changes wait this long (echoes coalesce). */
  const RELOAD_DEBOUNCE_MS = 400;

  let disposed = false;
  let loadSequence = 0;

  const languages = computed<ILanguage[]>(
    () => vueData.restaurantLanguages ?? []
  );
  const languageIds = computed(() => languages.value.map((lang) => lang.id));

  /** The sizes shown: the ones the texts were loaded for. */
  const loadedSizes = shallowRef<ISavedSize[]>([]);
  const loadedOptionId = ref<number | null>(null);
  const base = shallowRef<TTranslationTexts | null>(null);
  const form = ref<TTranslationTexts | null>(null);
  const loadFailed = ref(false);

  const name = computed(() => props.source.name ?? '');

  const dirty = computed(
    () =>
      props.canEdit &&
      !!base.value &&
      !!form.value &&
      changedTextKeys(base.value, form.value).length > 0
  );
  const missingName = computed(() =>
    form.value ? missingNameLanguages(form.value, languageIds.value) : []
  );
  const invalid = computed(() => missingName.value.length > 0);

  const status = computed(() => {
    if (!base.value) return null;
    return translationStatus({
      visible: props.source.visible,
      description: props.source.description ?? null,
      sizes: loadedSizes.value,
      texts: base.value,
      languageIds: languageIds.value,
    });
  });

  const STATUS_ICONS = {
    ready: 'mdi-check-circle',
    pending: 'mdi-autorenew',
    hidden: 'mdi-eye-off-outline',
  } as const;
  const statusIcon = computed(() =>
    status.value ? STATUS_ICONS[status.value] : null
  );

  //#region Load and save

  function loadEntityRows(id: number) {
    return props.entity === 'item'
      ? service.getAllByItem(props.restaurantId, id)
      : service.getAllByCategory(props.restaurantId, id);
  }

  async function load() {
    if (!languageIds.value.length) return;
    const ids = languageIds.value;
    const entityId = props.source.id;
    // Size texts need `TranslationsManage` to be read.
    const sizes = props.canEdit ? props.sizes : [];
    const optionId = sizes.length ? props.sizeOptionId ?? null : null;
    loadSequence += 1;
    const sequence = loadSequence;
    try {
      const [entityRows, sizeRows] = await Promise.all([
        loadEntityRows(entityId),
        optionId !== null
          ? service.getOptionTranslations(props.restaurantId, optionId)
          : Promise.resolve([]),
      ]);
      if (disposed || sequence !== loadSequence) return;
      const stored = translationTexts({
        entity: props.entity,
        entityId,
        entityRows,
        sizeRows,
        languageIds: ids,
        sizeIds: sizes.map((size) => size.id),
      });
      form.value = rebaseTexts(base.value, form.value, stored);
      base.value = stored;
      loadedSizes.value = sizes;
      loadedOptionId.value = optionId;
      loadFailed.value = false;
    } catch (error) {
      if (disposed || sequence !== loadSequence) return;
      console.error('Loading translations failed', error);
      // A failed reload keeps what is shown.
      if (!base.value) loadFailed.value = true;
    }
  }

  /** The editor's step 3: the entity's texts, then its sizes'. */
  async function save(entityId: number) {
    if (!base.value || !form.value) return;
    const changes = translationChanges(base.value, form.value, {
      languageIds: languageIds.value,
      sizeIds: loadedSizes.value.map((size) => size.id),
    });
    if (isEmptyTranslationChanges(changes)) return;
    if (changes.entries.length || changes.removedLanguageIds.length) {
      if (props.entity === 'item')
        await service.saveItemTranslations(
          props.restaurantId,
          entityId,
          changes.entries,
          changes.removedLanguageIds
        );
      else
        await service.saveCategoryTranslations(
          props.restaurantId,
          entityId,
          changes.entries,
          changes.removedLanguageIds
        );
    }
    // The product save may have removed sizes, or all of them.
    const liveIds = new Set(props.sizes.map((size) => size.id));
    const sizeEntries = changes.sizeEntries.filter(
      (entry) => entry.optionItemId !== null && liveIds.has(entry.optionItemId)
    );
    const optionId = props.sizeOptionId ?? null;
    if (
      sizeEntries.length &&
      optionId !== null &&
      optionId === loadedOptionId.value
    )
      await service.saveOptionTranslations(
        props.restaurantId,
        optionId,
        sizeEntries
      );
    if (!disposed) await load();
  }

  function reset() {
    if (base.value) form.value = { ...base.value };
  }

  const reload = useDebounceFn(() => {
    if (!disposed && base.value && !dirty.value) void load();
  }, RELOAD_DEBOUNCE_MS);

  // A rename or another device's change can outdate the texts.
  watch(
    () => props.source,
    (next, previous) => {
      if (next !== previous) void reload();
    }
  );

  let unregister: (() => void) | null = null;
  onMounted(() => {
    void load();
    if (props.canEdit)
      unregister = props.register({
        key: 'translations',
        dirty,
        invalid,
        save,
        reset,
      });
  });

  onBeforeUnmount(() => {
    disposed = true;
    unregister?.();
  });

  //#endregion

  function text(key: string): ITranslationText {
    return (
      form.value?.[key] ?? { title: null, description: null, outdated: false }
    );
  }

  function setText(key: string, value: ITranslationEntryModel) {
    if (!form.value) return;
    // An edited text is the owner's: it no longer waits for a translation.
    form.value = {
      ...form.value,
      [key]: {
        title: value.title,
        description: value.description,
        outdated: false,
      },
    };
  }

  const catalogSettingsPath = computed(() => {
    const stringId = restaurantStore.restaurant?.stringId;
    return stringId ? `/${stringId}/catalog/settings` : null;
  });
</script>

<template>
  <div class="v2-catalog-translations-settings">
    <p v-if="!languages.length" class="translations-note">
      {{ t('menuPage.translations.noLanguages') }} ·
      <router-link
        v-if="catalogSettingsPath"
        :to="catalogSettingsPath"
        class="translations-link"
        >{{ t('menuPage.translations.chooseLanguages') }}</router-link
      >
    </p>

    <div v-else-if="loadFailed" class="translations-failed">
      <p class="translations-note">
        {{ t('menuPage.translations.loadError') }}
      </p>
      <PosUiButton color="neutral" variant="outline" @click="load">{{
        t('menuPage.retry')
      }}</PosUiButton>
    </div>

    <div v-else-if="!form" class="translations-loading">
      <PosUiSkeleton class="loading-line is-short" />
      <PosUiSkeleton class="loading-block" />
    </div>

    <template v-else>
      <p
        v-if="status"
        class="translations-status"
        :class="`is-${status}`"
        role="status"
      >
        <i class="mdi status-icon" :class="statusIcon" aria-hidden="true" />
        {{ t(`menuPage.translations.status.${status}`) }}
      </p>
      <p class="translations-note">{{ t('menuPage.translations.help') }}</p>

      <section
        v-for="language in languages"
        :key="language.id"
        class="language-block"
      >
        <h4 class="language-name">{{ language.name }}</h4>
        <template v-if="canEdit">
          <V2TranslationEntryRow
            :language="language"
            :label="name"
            :value="text(language.id)"
            @input="setText(language.id, $event)"
          />
          <p
            v-if="submitted && missingName.includes(language.id)"
            class="entry-error"
            role="alert"
          >
            {{ t('menuPage.translations.nameRequired') }}
          </p>
          <V2TranslationEntryRow
            v-for="size in loadedSizes"
            :key="size.id"
            class="size-entry"
            :language="language"
            :label="size.title"
            :value="text(sizeTextKey(language.id, size.id))"
            @input="setText(sizeTextKey(language.id, size.id), $event)"
          />
        </template>
        <div v-else class="readonly-entry">
          <p class="readonly-title">
            {{ text(language.id).title || t('menuPage.translations.none') }}
          </p>
          <p v-if="text(language.id).description" class="readonly-description">
            {{ text(language.id).description }}
          </p>
        </div>
      </section>

      <p v-if="canEdit && hasNewSizes" class="translations-note">
        {{ t('menuPage.translations.newSizes') }}
      </p>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-translations-settings {
    .translations-note {
      @apply tw-m-0 tw-mt-1 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .translations-link {
      @apply tw-font-semibold tw-text-neutral-900 tw-underline;
    }

    .translations-failed {
      .translations-note {
        @apply tw-mb-2;
      }
    }

    .translations-loading {
      .loading-line {
        @apply tw-h-4 tw-w-40;
      }

      .loading-block {
        @apply tw-mt-3 tw-h-24 tw-w-full;
      }
    }

    .translations-status {
      @apply tw-m-0 tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium;

      .status-icon {
        @apply tw-mr-1 tw-text-sm;
      }

      &.is-ready {
        @apply tw-bg-emerald-50 tw-text-emerald-800;
      }

      &.is-pending {
        @apply tw-bg-amber-50 tw-text-amber-800;
      }

      &.is-hidden {
        @apply tw-bg-neutral-100 tw-text-neutral-600;
      }
    }

    .language-block {
      @apply tw-mt-4;

      .language-name {
        @apply tw-m-0 tw-mb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .size-entry {
        @apply tw-mt-2;
      }

      .entry-error {
        @apply tw-m-0 tw-mt-1 tw-text-xs tw-text-red-600;
      }
    }

    .readonly-entry {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-p-3;

      .readonly-title {
        @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
      }

      .readonly-description {
        @apply tw-m-0 tw-mt-1 tw-text-sm tw-text-neutral-600;
      }
    }
  }
</style>
